import React from 'react';
import { FaTimes } from 'react-icons/fa';

const MovieModal = ({ isOpen, onClose, movieData }) => {
  if (!isOpen || !movieData) return null;


  const getIMDbUrl = (url) => {
    if (!url) return "#";

    // Extract imdb id like tt1234567
    const match = url.match(/(tt\d+)/);
    if (match) {
      return `https://www.imdb.com/title/${match[1]}/`;
    }

    return url; // fallback
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">{movieData.name || 'Movie Details'}</h2>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-800">
            <FaTimes size={24} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Poster */}
          {movieData.poster && (
            <img
              src={movieData.poster}
              alt={`${movieData.name} poster`}
              className="w-full h-64 object-contain rounded-md mb-4"
            />
          )}

          {/* Title and Year */}
          <div>
            <h3 className="text-lg font-semibold">Title</h3>
            <p>{movieData.name || 'N/A'}</p>
            <h3 className="text-lg font-semibold mt-2">Year</h3>
            <p>{movieData.datePublished?.split('-')[0] || 'N/A'}</p>
          </div>

          {/* Runtime */}
          <div>
            <h3 className="text-lg font-semibold">Runtime</h3>
            <p>{movieData.duration ? parseISOToHHMMSS(movieData.duration) : 'N/A'}</p>
          </div>

          {/* Rating */}
          <div>
            <h3 className="text-lg font-semibold">IMDb Rating</h3>
            <p>
              {movieData.rating?.ratingValue
                ? `${movieData.rating.ratingValue} / ${movieData.rating.bestRating}`
                : 'N/A'}
            </p>
            {movieData.rating?.ratingCount && (
              <p className="text-sm text-gray-500">
                ({movieData.rating.ratingCount} votes)
              </p>
            )}
          </div>

          {/* Content Rating */}
          <div>
            <h3 className="text-lg font-semibold">Content Rating</h3>
            <p>{movieData.contentRating || 'N/A'}</p>
          </div>

          {/* Genres */}
          <div>
            <h3 className="text-lg font-semibold">Genres</h3>
            <p>
              {Array.isArray(movieData.genre)
                ? movieData.genre.join(', ')
                : movieData.genre || 'N/A'}
            </p>
          </div>

          {/* Cast */}
          <div>
            <h3 className="text-lg font-semibold">Cast</h3>
            <p>
              {Array.isArray(movieData.actor)
                ? movieData.actor.map((actor) => actor.name || actor).join(', ')
                : 'N/A'}
            </p>
          </div>

          {/* Directors */}
          <div>
            <h3 className="text-lg font-semibold">Directors</h3>
            <p>
              {Array.isArray(movieData.director)
                ? movieData.director.map((dir) => dir.name || dir).join(', ')
                : 'N/A'}
            </p>
          </div>

          {/* Creators */}
          <div>
            <h3 className="text-lg font-semibold">Creators</h3>
            <p>
              {Array.isArray(movieData.creator)
                ? movieData.creator.map((creator) => creator.name || creator).join(', ')
                : 'N/A'}
            </p>
          </div>

          {/* Tags/Keywords */}
          <div>
            <h3 className="text-lg font-semibold">Tags</h3>
            <p>
              {movieData.keywords && typeof movieData.keywords === 'string'
                ? movieData.keywords.split(',').map((tag) => tag.trim()).join(', ')
                : 'N/A'}
            </p>
          </div>

          {/* Synopsis */}
          <div>
            <h3 className="text-lg font-semibold">Synopsis</h3>
            <p>{movieData.description || 'N/A'}</p>
          </div>

          {/* IMDb URL */}
          <div>
            <h3 className="text-lg font-semibold">IMDb Link</h3>


            <a
              href={getIMDbUrl(movieData.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              View on IMDb
            </a>


          </div>

          {/* Review (if available) */}
          {movieData.review && (
            <div>
              <h3 className="text-lg font-semibold">Review</h3>
              <p>
                <strong>Author:</strong> {movieData.review.author || 'N/A'}<br />
                <strong>Date:</strong> {movieData.review.dateCreated || 'N/A'}<br />
                <strong>Content:</strong> {movieData.review.description || 'N/A'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function to convert ISO duration to HH:MM:SS
const parseISOToHHMMSS = (isoDuration) => {
  if (!isoDuration) return '';
  const match = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return '';
  const hours = match[1] ? match[1].padStart(2, '0') : '00';
  const minutes = match[2] ? match[2].padStart(2, '0') : '00';
  const seconds = match[3] ? match[3].padStart(2, '0') : '00';
  return `${hours}:${minutes}:${seconds}`;
};

export default MovieModal;